// https://theme-ui.com/theme-spec

/* Misc Notes:
  1. heading refers to h1, h2, h3, etc. Not the header/footer
  10. This file is executed once Serverside, and once Clientside
*/

/////    Brand Specific Color Settings    /////

const sprucePrimaryPalette = {

  primaries: {
    white: '#ffffff',
    black: '#000000',
    grey: '#faf6f1',
    blue: '#3c519d',
    lightBlue: '#7abae2',
    // Complementary
    blueDark: '#2f4080',
    lightBlueLight: '#e8f4fb',
    greyDark: '#e8e4de',
    darkBrown: '#333333',
    // Aliases for theme keys (beige/cream/taupe/rose)
    beige: '#faf6f1',
    cream: '#e8f4fb',
    taupe: '#e8e4de',
    rose: '#7abae2',
  },

  backgrounds: {
    white: '#ffffff',
    black: '#000000',
  },

  fonts: {
    poppins: 'Poppins, Helvetica, Arial, sans-serif',
    poppinsSemiBold: 'Poppins-SemiBold, Helvetica, Arial, sans-serif',
    poppinsBold: 'Poppins-Bold, Helvetica, Arial, sans-serif',
    poppinsBlackHeavy: 'Poppins-BlackHeavy, Helvetica, Arial, sans-serif',
    menlo: 'Menlo, monospace',
  },
  referenceColors: {
    text: '#333333',
    body: '#000',
    heading: '#000000',
    background: '#7abae2',
    primary: '#3c519d',
    secondary: '#7abae2',
    muted: '#e8e4de',
    navList: '#000',
    headerCTA: '#3c519d',
    link: '#3c519d',
  }
};

// Theme config exported for access in components, build loop, and builder configs
const havenStyles = {
  referenceColors: sprucePrimaryPalette.referenceColors,
  breakpoints: ['48em', '64em', '90em'],
  space: {
    none: '0',
    small: ['0.375em', '0.375em', '0.75em'], // Arrays set responsive values from smallest breakpoint to largest
    medium: ['0.5em', '0.5em', '1em'],
    large: ['0.75em', '0.75em', '1.5em'],
    xlarge: ['1.2em', '1.5em', '3em'],
    mega: ['4em', '4em', '8em'],
  },
  sizes: {
    container: ['100%', '43.25em', '61.25em', '76.25em'],
    viewHeight100: '100vh',
    viewWidth100: '100vw',
    navHeight: '75px',
    footerHeight: '250px',
  },
  fonts: {
    body: sprucePrimaryPalette.fonts.poppins,
    heading: sprucePrimaryPalette.fonts.poppinsSemiBold,
    monospace: sprucePrimaryPalette.fonts.menlo,
    nav: sprucePrimaryPalette.fonts.poppins,
  },
  fontSizes: [16, 18, 24, 19, 30, 24, 36, 36, 48, 32, 48],
  fontWeights: {
    body: 400,
    heading: 400,
    bold: 600,
  },
  lineHeights: {
    body: 1.75,
    heading: 1.25,
    button: 1.5,
  },
  letterSpacings: {
    body: 0,
    heading: 0,
    caps: '0.05em',
    caption: '0.02em',
  },
  // User exposed color options in the Builder UI are also drawn from this theme
  // Color must be defined here to be used in backgroundColors and other sub categories
  colors: {
    text: sprucePrimaryPalette.referenceColors.text,
    body: sprucePrimaryPalette.referenceColors.body,
    heading: sprucePrimaryPalette.referenceColors.heading,
    primary: sprucePrimaryPalette.primaries.blue,
    secondary: sprucePrimaryPalette.primaries.lightBlue,
    muted: sprucePrimaryPalette.primaries.greyDark,
    link: sprucePrimaryPalette.primaries.blue,

    blue: sprucePrimaryPalette.primaries.blue,
    lightBlue: sprucePrimaryPalette.primaries.lightBlue,
    grey: sprucePrimaryPalette.primaries.grey,
    beige: sprucePrimaryPalette.primaries.beige,
    cream: sprucePrimaryPalette.primaries.cream,
    taupe: sprucePrimaryPalette.primaries.taupe,
    brown: sprucePrimaryPalette.primaries.rose,
    darkBrown: sprucePrimaryPalette.primaries.darkBrown,

    white: sprucePrimaryPalette.primaries.white,
    black: sprucePrimaryPalette.primaries.black,
    offwhite: sprucePrimaryPalette.primaries.grey,

    transparent: 'transparent',
  },
  // Used for component background colors
  backgroundColors: {
    blue: sprucePrimaryPalette.primaries.blue,
    lightBlue: sprucePrimaryPalette.primaries.lightBlue,
    grey: sprucePrimaryPalette.primaries.grey,
    beige: sprucePrimaryPalette.primaries.beige,
    cream: sprucePrimaryPalette.primaries.cream,
    taupe: sprucePrimaryPalette.primaries.taupe,
    brown: sprucePrimaryPalette.primaries.rose,
    white: sprucePrimaryPalette.primaries.white,
    black: sprucePrimaryPalette.primaries.black,
    transparent: 'transparent',
  },
  radii: {
    card: ['0.25em', '0.5em'],
    button: '0',
  },
  shadows: {
    card: '0 0.125em 0.1875em 0 rgba(0,0,0,0.2)',
  },
  text: {
    body: {
      fontFamily: 'body',
      fontWeight: 'body',
      lineHeight: 'body',
      letterSpacing: 'body',
    },
    heading: {
      fontFamily: 'heading',
      fontWeight: 'heading',
      lineHeight: 'heading',
      letterSpacing: 'heading',
    },
  },
  // theme variants
  buttons: {
    primary: {
      appearance: 'none',
      alignItems: 'center',
      bg: '#3c519d',
      border: 0,
      borderRadius: '0',
      color: '#ffffff',
      display: 'inline-flex',
      fontFamily: sprucePrimaryPalette.fonts.lato,
      fontSize: '14px',
      fontStyle: 'normal',
      fontWeight: '400',
      justifyContent: 'center',
      lineHeight: 'button',
      letterSpacing: '0.05em',
      marginBottom: 'medium',
      padding: ['0.875em 1.75em', '0.875em 2em'],
      position: 'relative',
      verticalAlign: 'middle',
      whiteSpace: 'nowrap',
      transition: 'background-color 0.3s ease',
      cursor: 'pointer',
      textTransform: 'uppercase',

      '&:hover': {
        bg: '#2f4080',
        transition: 'background-color 0.3s ease',
      },
      '&:focus': {
        outline: 'none',
        boxShadow: '0 0 0 3px rgba(60, 81, 157, 0.3)',
      },
      '& + a': {
        marginLeft: ['none', 'none', 'small'],
      },
    },
    secondary: {
      variant: 'buttons.primary',
      bg: '#7abae2',
      color: '#000000',
      border: 0,
      '&:hover': {
        bg: '#3c519d',
        color: '#ffffff',
        transition: 'background-color 0.3s ease',
      },
      '&:focus': {
        outline: 'none',
        boxShadow: '0 0 0 3px rgba(122, 186, 226, 0.3)',
      },
    },
    beige: {
      variant: 'buttons.primary',
      bg: '#faf6f1',
      color: '#333333',
      border: '1px solid #e8e4de',
      '&:hover': {
        bg: '#e8e4de',
        borderColor: '#e8e4de',
        transition: 'background-color 0.3s ease, border-color 0.3s ease',
      },
      '&:focus': {
        outline: 'none',
        boxShadow: '0 0 0 3px rgba(60, 81, 157, 0.2)',
      },
    },
    blackBorder: {
      variant: 'buttons.primary',
      bg: 'transparent',
      color: '#000000',
      border: '1px solid #000000',
      '&:hover': {
        bg: '#faf6f1',
        borderColor: '#000000',
        transition: 'background-color 0.3s ease',
      },
      '&:focus': {
        outline: 'none',
        boxShadow: '0 0 0 3px rgba(0, 0, 0, 0.2)',
      },
    },
    link: {
      variant: 'buttons.primary',
      paddingLeft: 0,
      paddingRight: 0,
      bg: 'transparent',
      color: '#333333',
      border: 'none',
      textDecoration: 'underline',
      '&:hover': {
        bg: 'transparent',
        color: '#3c519d',
      },
    },
    darkBrown: {
      variant: 'buttons.primary',
      bg: '#3c519d',
      color: '#ffffff',
      border: 0,
      '&:hover': {
        bg: '#2f4080',
        transition: 'background-color 0.3s ease',
      },
      '&:focus': {
        outline: 'none',
        boxShadow: '0 0 0 3px rgba(60, 81, 157, 0.3)',
      },
    },
  },
  // markdown styles
  styles: {
    root: {
      header: {
        fontSize: '11px',
      },
      variant: 'text.body',
      h1: {
        variant: 'text.heading',
        fontWeight: '200',
        fontSize: ['32px', '48px'],
        fontFamily: sprucePrimaryPalette.fonts.poppinsSemiBold, 
        lineHeight: '1.2',
        letterSpacing: '0',
        textTransform: 'none',
        margin: '0 0 1rem 0',
      },
      h2: {
        variant: 'text.heading',
        fontSize: ['28px', '36px'],
        fontFamily: sprucePrimaryPalette.fonts.poppinsSemiBold, 
        fontWeight: '200',
        lineHeight: '1.3',
        letterSpacing: '0',
        margin: '0 0 0.75rem 0',
      },
      h3: {
        variant: 'text.heading',
        fontSize: ['24px', '30px'],
        fontFamily: sprucePrimaryPalette.fonts.poppinsSemiBold,
        fontWeight: '500',
        lineHeight: '1.4',
        letterSpacing: '0',
        margin: '0 0 0.5rem 0',
      },
      h4: {
        variant: 'text.heading',
        fontSize: ['22px', '28px'],
        fontFamily: sprucePrimaryPalette.fonts.poppins,
        lineHeight: '1.4',
        letterSpacing: '0',
        fontWeight: '500',
        margin: '0 0 0.5rem 0',
      },
      h5: {
        variant: 'text.heading',
        fontSize: ['18px', '22px'],
        fontFamily: sprucePrimaryPalette.fonts.posppins,
        lineHeight: '1.5',
        fontWeight: '600',
        letterSpacing: '0',
        margin: '0 0 0.5rem 0',
      },
      h6: {
        variant: 'text.heading',
        fontSize: ['16px', '18px'],
        fontFamily: sprucePrimaryPalette.fonts.poppins,
        fontWeight: '500',
        lineHeight: '1.5',
        letterSpacing: '0',
        margin: '0 0 0.5rem 0',
        textTransform: 'none'
      },
      // Utility classes to apply heading styles to any element
      // Usage: <h2 class="header-h5"> will be styled as h5
      '.header-h1': {
        variant: 'text.heading',
        fontWeight: '300',
        fontSize: ['32px', '48px'],
        fontFamily: sprucePrimaryPalette.fonts.playfair, 
        lineHeight: '1.2',
        letterSpacing: '0',
        textTransform: 'none',
        margin: '0 0 1rem 0',
      },
      '.header-h2': {
        variant: 'text.heading',
        fontSize: ['28px', '36px'],
        fontFamily: sprucePrimaryPalette.fonts.playfair, 
        fontWeight: '400',
        lineHeight: '1.3',
        letterSpacing: '0',
        margin: '0 0 0.75rem 0',
      },
      '.header-h3': {
        variant: 'text.heading',
        fontSize: ['24px', '30px'],
        fontFamily: sprucePrimaryPalette.fonts.lato,
        fontWeight: '400',
        lineHeight: '1.4',
        letterSpacing: '0',
        margin: '0 0 0.5rem 0',
      },
      '.header-h4': {
        variant: 'text.heading',
        fontSize: ['22px', '28px'],
        fontFamily: sprucePrimaryPalette.fonts.lato,
        lineHeight: '1.4',
        letterSpacing: '0',
        fontWeight: '600',
        margin: '0 0 0.5rem 0',
      },
      '.header-h5': {
        variant: 'text.heading',
        fontSize: ['18px', '22px'],
        fontFamily: sprucePrimaryPalette.fonts.lato,
        lineHeight: '1.5',
        fontWeight: '600',
        letterSpacing: '0',
        margin: '0 0 0.5rem 0',
      },
      '.header-h6': {
        variant: 'text.heading',
        fontSize: ['16px', '18px'],
        fontFamily: sprucePrimaryPalette.fonts.lato,
        fontWeight: '400',
        lineHeight: '1.5',
        letterSpacing: '0',
        margin: '0 0 0.5rem 0',
        textTransform: 'none'
      },
      p: {
        lineHeight: ['1.6', '1.75'],
        fontSize: '16px',
        fontFamily: sprucePrimaryPalette.fonts.lato,
        letterSpacing: '0',
        margin: 0,
        color: 'body',
      },
      a: {
        color: 'link',
      },
      table: {
        borderCollapse: 'collapse',
        borderSpacing: 0,
      },
      tr: {
        fontSize: '1.1em',
      },
      th: {
        color: 'heading',
        padding: '0.4em',
        borderBottom: '2px solid rgba(0,0,0,0.2)',
        textTransform: 'uppercase',
        verticalAlign: 'middle',
      },
      td: {
        borderBottom: '1px solid rgba(0,0,0,0.1)',
        padding: '0.4em',
        lineHeight: '1.8em',
        verticalAlign: 'middle',
        '& p': {
          fontSize: '80%',
          margin: '0.4em 0',
          lineHeight: '1.3',
        },
      },
    },
    textStyles: ['default', 'quote'],
    textColors: ['default', 'black', 'white', 'blue', 'lightBlue', 'brown', 'darkBrown', 'taupe', 'grey'],
    default: {
      h1: { 
        color: 'heading', 
      },
      h2: { 
        color: 'heading', 
      },
      h3: { 
        color: 'heading',
      },
      h4: { 
        color: 'heading',
      },
      h5: { 
        color: 'heading',
      },
      h6: { 
        color: 'heading'
      },
      p: {
        color: 'body'
      }
    },
    quote: {
      borderLeft: 'solid 4px #3c519d',
      padding: '10px 20px',
      color: 'body',
      fontFamily: sprucePrimaryPalette.fonts.lato,
      lineHeight: '1.5',
      fontStyle: 'normal',
    }
  },
};

export default havenStyles;